import { useAuthStore } from '../store/authStore';

interface RequestOptions extends RequestInit {
  data?: any;
  params?: Record<string, string>;
}

class HttpClient {
  private getAuthToken(): string | null {
    return useAuthStore.getState().userToken;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorBody.message || 'An API error occurred');
    }
    return response.json();
  }

  private buildUrl(path: string, params?: Record<string, string>): string {
    const url = new URL(path, window.location.origin);
    if (params) {
      Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));
    }
    return url.toString();
  }

  private async request<TResponse>(method: string, path: string, options?: RequestOptions): Promise<TResponse> {
    const { data, params, ...restOptions } = options || {};
    const url = this.buildUrl(path, params);
    const token = this.getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      method,
      headers,
      ...restOptions,
    };

    if (data && method !== 'DELETE') {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(url, config);
    return this.handleResponse(response);
  }

  public get<TResponse>(path: string, options?: RequestOptions): Promise<TResponse> {
    return this.request<TResponse>('GET', path, options);
  }

  public post<TRequest, TResponse>(path: string, data?: TRequest, options?: RequestOptions): Promise<TResponse> {
    return this.request<TResponse>('POST', path, { ...options, data });
  }

  public put<TRequest, TResponse>(path: string, data?: TRequest, options?: RequestOptions): Promise<TResponse> {
    return this.request<TResponse>('PUT', path, { ...options, data });
  }

  public delete<TResponse>(path: string, options?: RequestOptions): Promise<TResponse> {
    return this.request<TResponse>('DELETE', path, options);
  }
}

const apiService = new HttpClient();

export default apiService;
